import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { z } from "zod";
import ReactMarkdown from "react-markdown";
import { useState } from "react";

const SuggestMeal = () => {
    const { toast } = useToast();
    const { t, i18n } = useTranslation();
    const { authFetch } = useAuth();
    const [suggestion, setSuggestion] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const suggestMealSchema = z.object({
        mealType: z.string().min(1, t("meals.form.mealType.error")),
        location: z.string().min(1, t("meals.form.location.error")),
        language: z.string().default(i18n.language),
    });

    type SuggestMealFormData = z.infer<typeof suggestMealSchema>;

    const form = useForm<SuggestMealFormData>({
        resolver: zodResolver(suggestMealSchema),
        defaultValues: {
            mealType: "",
            location: "",
            language: i18n.language,
        },
    });

    const onSubmit = async (data: SuggestMealFormData) => {
        setLoading(true);
        setSuggestion(null);

        try {
            const response = await authFetch("/api/meals/suggest", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(t("meals.form.error"));
            }

            const result = await response.json();
            setSuggestion(result.suggestion); // Assumendo che l'API ritorni il suggerimento in Markdown
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("meals.suggetion.toast.title"),
                description: error instanceof Error ? error.message : t("meals.suggetion.toast.error"),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />

            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">{t("meals.suggestion.title")}</h1>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            {/* Tipo di pasto */}
                            <FormField
                                control={form.control}
                                name="mealType"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.mealType")}</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("meals.form.selectMealType")} />
                                                </SelectTrigger>
                                            </FormControl>

                                            <SelectContent>
                                                {["breakfast", "morningSnack", "lunch", "afternoonSnack", "dinner"].map((mealType) => (
                                                    <SelectItem key={mealType} value={mealType}>
                                                        {t(`meals.types.${mealType}`)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />

                            {/* Posizione */}
                            <FormField
                                control={form.control}
                                name="location"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.location")}</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("meals.locations.placeholder")} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {["home", "restaurant", "work", "walk"].map((location) => (
                                                    <SelectItem key={location} value={location}>
                                                        {t(`meals.locations.${location}`)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? t("meals.suggestion.loading") : t("meals.suggestion.submit")}
                            </Button>
                        </form>
                    </Form>

                    {loading && (
                        <div className="container mx-auto p-6">
                            <div className="animate-pulse space-y-4">
                                <div className="h-32 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    )}

                    {/* Visualizzazione del suggerimento */}
                    {suggestion && (
                        <div className="mt-6 p-4 border border-gray-300 rounded-lg bg-white prose">
                            {/*<h2 className="text-xl font-semibold mb-4">{t("meals.suggestion.proposal")}</h2>*/}
                            <ReactMarkdown>{suggestion}</ReactMarkdown>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SuggestMeal;
